'use client';

import {
  cn,
  generateRdsId,
  InputAdornment,
  InputErrorIcon,
  inputHeightVariants,
  type InputProps,
  inputStyles,
  inputThemeableVars,
  Tag,
  useFieldContext,
} from '@ctct/summit-core';
import { cva } from 'class-variance-authority';
import { type ComponentPropsWithoutRef, type KeyboardEvent, useState } from 'react';

import { TagInputField } from './TagInputField';

const TAGINPUT_RDS_ID = generateRdsId('tag-input');

interface TagInputItem {
  id: string;
  label: string;
  hasError?: boolean;
}

const { dimension, ...restThemeableVars } = inputThemeableVars;
const themeableVars = Object.values(restThemeableVars).flat();

const disabledClasses = `
  r:bg-(--bg-disabled)
  r:border-(--border-disabled)
  r:text-(--text-disabled)
`;

const tagInputVariants = cva(
  `
    ${themeableVars.join(' ')}
    ${inputStyles.base}
    ${inputStyles.disabled}
    ${inputStyles.focus}
    ${inputStyles.textfield}
    r:min-h-(--height)
    r:relative
    r:flex
    r:flex-wrap
    r:items-center
    r:gap-x-2
    r:gap-y-4
    r:leading-none
  `,
  {
    variants: {
      variant: inputHeightVariants,
    },
    defaultVariants: {
      variant: 'md',
    },
  }
);

interface TagInputProps extends ComponentPropsWithoutRef<'div'> {
  onAddTag: (text: string) => void;
  onRemoveTag: (tagId: string) => void;
  tags?: TagInputItem[];
  variant?: InputProps['variant'];
  placeholder?: string;
  isDisabled?: boolean;
}

const TagInput = ({
  onAddTag,
  onRemoveTag,
  tags = [],
  variant = 'md',
  placeholder,
  isDisabled,
  className,
  ...props
}: TagInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const { inputId, labelId, state } = useFieldContext();
  const isEffectivelyDisabled = isDisabled ?? state === 'disabled';
  const hasEffectiveError = tags.some((tag) => tag.hasError) || state === 'error';
  const tagSize = variant === 'lg' ? 'lg' : 'sm';

  const commitTag = () => {
    const trimmedText = inputValue.trim();
    if (trimmedText === '') return false;

    onAddTag(trimmedText);
    setInputValue('');
    return true;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;

    if (e.key === 'Enter') {
      const hasCommitted = commitTag();
      if (hasCommitted) e.preventDefault();
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      e.preventDefault();
      const lastTag = tags[tags.length - 1];
      onRemoveTag(lastTag.id);
    }
  };

  const handleRemove = (tagId: string) => {
    onRemoveTag(tagId);
  };

  const handleBlur = () => {
    commitTag();
  };

  const containerClasses = cn(
    TAGINPUT_RDS_ID,
    tagInputVariants({ variant }),
    hasEffectiveError && inputStyles.error,
    hasEffectiveError && 'r:pr-10',
    !hasEffectiveError && 'r:focus-within:border-(--border-active)',
    isEffectivelyDisabled && disabledClasses,
    className
  );

  return (
    <div className={containerClasses} data-sds-id="tag-input" {...props}>
      {tags.map((tag) => {
        const { id, label, hasError } = tag;
        return (
          <Tag
            key={id}
            size={tagSize}
            color={hasError ? 'error' : 'neutral'}
            actionMode="remove"
            onRemove={() => handleRemove(id)}
            isDisabled={isEffectivelyDisabled}
            data-testid={`${TAGINPUT_RDS_ID}-entry`}
          >
            {label}
          </Tag>
        );
      })}
      <TagInputField
        id={inputId}
        aria-labelledby={labelId}
        value={inputValue}
        placeholder={placeholder}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={isEffectivelyDisabled}
      />
      {hasEffectiveError && (
        <InputAdornment position="end" adornment={<InputErrorIcon className="r:mr-3" />} />
      )}
    </div>
  );
};

export { TagInput, TAGINPUT_RDS_ID, type TagInputItem, type TagInputProps };
